import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getCommonTeams } from '../services/individualBioService';
import { SortConfig, Team } from '../types/thematicGroups';

interface UseThematicGroupsProps {
  userUuid1: string;
  userUuid2: string;
  enabled?: boolean;
}

interface UseThematicGroupsReturn {
  commonTeams: Team[];
  otherTeams: Team[];
  allTeams: Team[];
  loading: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  sortedTeams: Team[];
}

const useThematicGroups = ({
  userUuid1,
  userUuid2,
  enabled = true,
}: UseThematicGroupsProps): UseThematicGroupsReturn => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'in_common',
    direction: 'desc',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['thematicGroups', userUuid1, userUuid2],
    queryFn: () => getCommonTeams(userUuid1, userUuid2),
    enabled: enabled && !!userUuid1 && !!userUuid2,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const commonTeams = data?.common_teams || [];
  const otherTeams = data?.other_teams || [];
  const allTeams = useMemo(() => [...commonTeams, ...otherTeams], [commonTeams, otherTeams]);

  // Memoize the sorted teams computation
  const sortedTeams = useMemo(() => {
    return [...allTeams].sort((a, b) => {
      // Primeiro, separa grupos em comum dos outros grupos
      const aIsCommon = commonTeams.some((commonTeam: Team) => commonTeam.uuid === a.uuid);
      const bIsCommon = commonTeams.some((commonTeam: Team) => commonTeam.uuid === b.uuid);

      // Se um é comum e o outro não, o comum vem primeiro
      if (aIsCommon && !bIsCommon) return -1;
      if (!aIsCommon && bIsCommon) return 1;

      // Se ambos são do mesmo tipo (comum ou não comum), ordena pelo critério selecionado
      let aValue: string | number;
      let bValue: string | number;

      switch (sortConfig.field) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'total_members':
          aValue = a.total_members;
          bValue = b.total_members;
          break;
        case 'in_common':
          aValue = a.in_common_connections.length;
          bValue = b.in_common_connections.length;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (sortConfig.direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else {
        if (sortConfig.direction === 'asc') {
          return (aValue as number) - (bValue as number);
        } else {
          return (bValue as number) - (aValue as number);
        }
      }
    });
  }, [allTeams, sortConfig, commonTeams]);

  return {
    commonTeams,
    otherTeams,
    allTeams,
    loading: isLoading,
    error: error?.message || null,
    sortConfig,
    setSortConfig,
    sortedTeams,
  };
};

export default useThematicGroups;
